import { create } from '@bufbuild/protobuf';

import { IBooleanResponse, ILinkPreviewModel } from '@gem/models/commonModel';
import { logger } from '@gem/modules/logger';

import { mapILinkPreviewModel } from 'APP/model/common/commonModel.b2b.mapper';
import { EmptyResponseError } from 'APP/model/error/errors/EmptyResponseError';
import { WsRequestError } from 'APP/model/error/errors/WsRequestError';
import {
  mapIForwardMessagesResponse,
  mapIMessage,
  mapMessageDeleteMode,
  mapMessageDirection,
  mapMessageMediaType,
  mapToSendMessagePayloadData,
  mapMessagesCountsResponse,
  mapToSendMessageQuotedMessageData,
  mapSaveScheduledMessageRequest,
  mapUpdateScheduledMessageRequest,
  mapScheduledMessageToIScheduledMessage,
  mapScheduledMessagesResponse,
} from 'APP/model/message/messageModel.b2b.mapper';
import { ServerErrorCode } from 'APP/restApi/constants/constants';
import { GroupServerErrorCode } from 'APP/restApi/constants/group.errors';
import {
  confirmReadChatMessagesRequest,
  deleteMessagesRequest,
  forwardMessagesRequest,
  getMessagesByIdsRequest,
  getMessagesByMediaTypesRequest,
  getMessagesRequest,
  getRangeAroundMessageRequest,
  getRangeAroundNextUserMentionRequest,
  getRangeMessageByTsRequest,
  sendMessageRequest,
  subscribeOnMessages,
  updateMessageRequest,
  getLinkPreviewRequest,
  getUsersViewedMessageRequest,
  addMessageReactionRequest,
  removeMessageReactionRequest,
  getMessagesCountsRequest,
  scheduleMessageRequest,
  updateScheduledMessageRequest,
  getScheduledMessagesRequest,
  getScheduledMessagesByIdsRequest,
  deleteScheduledMessagesRequest,
  sendScheduledMessageNowRequest,
} from 'APP/wsApi/wsAppApi/requests/messageRequests';
import { MessageKeySchema } from 'APP/wsApi/wsAppApi/types/message/message_model_pb';
import { ForwardMessagesRequest_ForwardDataSchema } from 'APP/wsApi/wsAppApi/types/message/message_requests_pb';

import { buildModelError } from '../error/errorBuilder';
import { DEFAULT_LIMIT_OF_MESSAGES_LOADING } from './messageModel.constants';
import {
  GetMessagesDirection,
  IDeleteMessageDraftsRequest,
  IDeleteMessagesRequest,
  IDeleteScheduledMessagesRequest,
  IForwardMessagesResponse,
  IForwardRequest,
  IGetMessageDraftRequest,
  IGetMessageDraftsRequest,
  IGetMessageRequest,
  IGetMessagesByMediaTypesRequest,
  IGetMessagesRequest,
  IGetMessagesResponse,
  IGetUsersViewedMessageRequest,
  IGetUsersViewedMessageResponse,
  IMessage,
  IMessageDraftsAndTsResponse,
  IMessageDraftsResponse,
  IMessagesResponse,
  ISaveMessageDraftRequest,
  IScheduledMessage,
  ISendMessageResponse,
  ISendNewMessageRequest,
  ISubscribeUpdateMessagesRequest,
  ITranslateMessageRequest,
  IUpdateMessageDraftRequest,
  IUpdateMessageReaction,
  IUpdateMessageRequest,
  IUpdateScheduledMessageRequest,
  MessageModelErrorCode,
  IGetMessagesByMediaTypesCountRequest,
  IGetMessagesByMediaTypesCountResponse,
  ISendScheduledMessageRequest,
  IGetScheduledMessagesResponse,
} from './messageModel.types';

export async function getMessages(data: IGetMessagesRequest): Promise<IGetMessagesResponse> {
  try {
    const response = await getMessagesRequest({
      chatId: BigInt(data.groupId),
      cursorTs: data.cursorTs ? BigInt(data.cursorTs) : undefined,
      count: data.count,
      direction: mapMessageDirection(data.direction),
    });

    if (response.type.case === 'success') {
      return {
        messages: response.type.value.items.map(mapIMessage) || [],
        maxTs: response.type.value.maxTs?.toString(),
        minTs: response.type.value.minTs?.toString(),
        cursorTs: response.type.value.cursorTs?.toString(),
      };
    } else {
      return {
        messages: [],
      };
    }
  } catch (error) {
    logger.get('API').error('messaging.getMessagesRequest', error);
    throw buildModelError(error);
  }
}

export async function getGroupMessages(
  groupId: string,
  startTs: number,
  count: number = DEFAULT_LIMIT_OF_MESSAGES_LOADING
): Promise<IGetMessagesResponse> {
  try {
    return getMessages({
      groupId,
      count,
      cursorTs: startTs.toString(),
      direction: GetMessagesDirection.Newest,
    });
  } catch (error) {
    logger.get('API').error('messaging.getGroupMessages', error);
    throw buildModelError(error);
  }
}

export async function getMessage(groupId: string, messageId: string): Promise<IMessage> {
  try {
    const result = await getMessagesByIds(groupId, [messageId]);
    return result[0];
  } catch (error) {
    logger.get('API').error('messaging.getMessage', error);
    throw buildModelError(error);
  }
}

export async function getGroupHistoricalMessages(
  groupId: string,
  timestamp: number,
  limit: number = DEFAULT_LIMIT_OF_MESSAGES_LOADING
): Promise<IGetMessagesResponse> {
  try {
    return getMessages({
      groupId,
      count: limit,
      cursorTs: timestamp.toString(),
      direction: GetMessagesDirection.Oldest,
    });
  } catch (error) {
    logger.get('API').error('messaging.getGroupHistoricalMessages', error);
    throw buildModelError(error);
  }
}

// TODO: не хватает метода, надо на беке реализовать этот метод
export async function getRangeMessageByTs(
  groupId: string,
  timestamp: number,
  halfLimit: number = DEFAULT_LIMIT_OF_MESSAGES_LOADING
): Promise<IMessagesResponse> {
  try {
    const response = await getRangeMessageByTsRequest({
      chatId: BigInt(groupId),
      ts: BigInt(timestamp),
      halfLimit,
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
    }

    return {
      messages: response.type.value?.items.map(mapIMessage) || [],
      users: [],
      groups: [],
    };
  } catch (error) {
    logger.get('API').error('messaging.getRangeMessageByTs', error);
    throw buildModelError(error);
  }
}

export async function getRangeAroundNextUserMention(
  groupId: string,
  startTs: number,
  halfLimit = 50
): Promise<IMessage[]> {
  try {
    const response = await getRangeAroundNextUserMentionRequest({
      chatId: BigInt(groupId),
      startTs: BigInt(startTs),
      halfLimit,
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
      case 'unsupportedChatType':
        throw new WsRequestError(GroupServerErrorCode.UnsupportedChatType, response);
      case 'messageDoesNotExist':
        throw new WsRequestError(MessageModelErrorCode.MessageDoesNotExist, response);
    }

    return response.type.value?.items.map(mapIMessage) || [];
  } catch (error) {
    logger.get('API').error('messaging.getRangeAroundNextUserMention', error);
    throw buildModelError(error);
  }
}

export async function addMessageReaction(data: IUpdateMessageReaction): Promise<IMessage> {
  try {
    const result = await addMessageReactionRequest({
      chatId: BigInt(data.groupId),
      messageId: BigInt(data.messageId),
      reactionId: BigInt(data.reactionId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'reactOnSystemMessageIsForbidden':
        throw new WsRequestError(MessageModelErrorCode.ReactOnSystemMessageIsForbidden, result);
      case 'messageDoesNotExist':
        throw new WsRequestError(MessageModelErrorCode.MessageDoesNotExist, result);
    }

    return mapIMessage(result.type.value!.message!);
  } catch (error) {
    logger.get('API').error('messaging.updateMessageReaction', error);
    throw buildModelError(error);
  }
}

export async function removeMessageReaction(data: IUpdateMessageReaction): Promise<IMessage> {
  try {
    const result = await removeMessageReactionRequest({
      chatId: BigInt(data.groupId),
      messageId: BigInt(data.messageId),
      reactionId: BigInt(data.reactionId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
      case 'reactOnSystemMessageIsForbidden':
        throw new WsRequestError(MessageModelErrorCode.ReactOnSystemMessageIsForbidden, result);
      case 'messageDoesNotExist':
        throw new WsRequestError(MessageModelErrorCode.MessageDoesNotExist, result);
    }

    return mapIMessage(result.type.value!.message!);
  } catch (error) {
    logger.get('API').error('messaging.updateMessageReaction', error);
    throw buildModelError(error);
  }
}

export async function forwardMessage(data: IForwardRequest): Promise<IForwardMessagesResponse> {
  try {
    const response = await forwardMessagesRequest({
      chatId: BigInt(data.sourceGroupId),
      messagesToForward: data.messagesToForward.map((item) =>
        create(ForwardMessagesRequest_ForwardDataSchema, {
          messageId: BigInt(item.sourceMessageId),
          clientUuid: item.clientUuid,
          clientTs: BigInt(item.clientTs),
          payloadIds: item.payloadIds,
          targetChatId: item.targetGroupId ? BigInt(item.targetGroupId) : undefined,
          targetContactId: item.targetContactId ? BigInt(item.targetContactId) : undefined,
        })
      ),
    });

    return mapIForwardMessagesResponse(response);
  } catch (error) {
    logger.get('API').error('messaging.forwardMessage', error);
    throw buildModelError(error);
  }
}

export async function confirmReadGroupMessages(
  groupId: string,
  messageId: string
): Promise<IBooleanResponse> {
  try {
    const result = await confirmReadChatMessagesRequest({
      chatId: BigInt(groupId),
      messageId: BigInt(messageId),
    });

    return result.type.case === 'success'
      ? { result: result.type.value.success }
      : { result: false };
  } catch (error) {
    logger.get('API').error('messaging.confirmReadGroupMessages', error);
    throw buildModelError(error);
  }
}

export async function deleteMessages(data: IDeleteMessagesRequest): Promise<boolean> {
  try {
    const result = await deleteMessagesRequest({
      chatId: BigInt(data.groupId),
      messageIds: data.messageIds.map((id) => BigInt(id)),
      mode: mapMessageDeleteMode(data.mode),
    });

    return result.type.case === 'success' ? result.type.value.success : false;
  } catch (error) {
    logger.get('API').error('messaging.deleteMessages', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
export async function getAllScheduledMessages(): Promise<IScheduledMessage[]> {
  try {
    return [];
  } catch (error) {
    logger.get('API').error('messaging.getAllScheduledMessages', error);
    throw buildModelError(error);
  }
}

export async function sendNewMessage(
  message: ISendNewMessageRequest
): Promise<ISendMessageResponse> {
  try {
    const response = await sendMessageRequest({
      chatId: BigInt(message.groupId),
      clientTs: BigInt(message.clientTime),
      quotedMessageData: mapToSendMessageQuotedMessageData(message.quotedMessageData),
      clientUuid: message.clientUuid,
      payload: mapToSendMessagePayloadData(message.payload),
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
      case 'articleIsNotAllowed':
        throw new WsRequestError(MessageModelErrorCode.ArticleIsNotAllowed, response);
      case 'noPermissionsToSendMessage':
        throw new WsRequestError(MessageModelErrorCode.NoPermissionsToSendMessage, response);
      case 'maxAlbumItemsLimitReached':
        throw new WsRequestError(MessageModelErrorCode.MaxAlbumItemsLimitReached, response);
      case 'quotedMessageIsNotExist':
        throw new WsRequestError(MessageModelErrorCode.QuotedMessageIsNotExist, response);
      case 'e2eeCommitSyncRequired':
        throw new WsRequestError(MessageModelErrorCode.SecretMessageCommitSyncRequired, response);
    }

    if (response.type.case !== 'success' || !response.type.value?.message) {
      throw new EmptyResponseError('sendMessage', response);
    }

    return {
      message: mapIMessage(response.type.value.message),
      previousMessageTs: Number(response.type.value.previousMessageTs),
    };
  } catch (error) {
    logger.get('API').error('messaging.sendNewMessage', error);
    throw buildModelError(error);
  }
}

export async function sendScheduledMessageNow(
  groupId: string,
  messageId: string
): Promise<ISendMessageResponse> {
  try {
    const response = await sendScheduledMessageNowRequest({
      chatId: BigInt(groupId),
      messageId: BigInt(messageId),
    });

    if (response.type.case !== 'success' || !response.type.value?.message) {
      throw new EmptyResponseError('sendMessage', response);
    }

    return {
      message: mapIMessage(response.type.value.message),
      previousMessageTs: Number(response.type.value.previousMessageTs),
    };
  } catch (error) {
    logger.get('API').error('messaging.sendScheduledMessageNow', error);
    throw buildModelError(error);
  }
}

export async function updateMessage(message: IUpdateMessageRequest): Promise<IMessage> {
  try {
    const response = await updateMessageRequest({
      chatId: BigInt(message.groupId),
      messageId: BigInt(message.messageId),
      payload: mapToSendMessagePayloadData(message.payload),
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
      case 'noPermissionsToSendMessage':
        throw new WsRequestError(MessageModelErrorCode.NoPermissionsToSendMessage, response);
      case 'messageDoesNotExist':
        throw new WsRequestError(MessageModelErrorCode.MessageDoesNotExist, response);
      case 'hasNoPermissionsToUpdateMessage':
        throw new WsRequestError(MessageModelErrorCode.HasNoPermissionsToUpdateMessage, response);
    }

    if (!response.type.value?.message) {
      throw new EmptyResponseError('updateMessage', response);
    }

    return mapIMessage(response.type.value.message);
  } catch (error) {
    logger.get('API').error('messaging.updateMessage', error);
    throw buildModelError(error);
  }
}

export async function getRangeAroundMessage(
  data: IGetMessageRequest
): Promise<IGetMessagesResponse> {
  try {
    const response = await getRangeAroundMessageRequest({
      messageId: BigInt(data.messageId),
      chatId: BigInt(data.groupId),
      halfLimit: data.halfLimit,
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
      case 'messageDoesNotExist':
        throw new WsRequestError(MessageModelErrorCode.MessageDoesNotExist, response);
    }

    return {
      messages: response.type.value?.items.map(mapIMessage) || [],
      minTs: response.type.value?.minTs?.toString(),
      maxTs: response.type.value?.maxTs?.toString(),
    };
  } catch (error) {
    logger.get('API').error('messaging.getRangeAroundMessage', error);
    throw buildModelError(error);
  }
}

export async function subscribeUpdateMessages(
  data: ISubscribeUpdateMessagesRequest
): Promise<boolean> {
  try {
    const result = await subscribeOnMessages({
      messageKeys: data.map(({ messageId, groupId }) =>
        create(MessageKeySchema, {
          messageId: BigInt(messageId),
          chatId: BigInt(groupId),
        })
      ),
    });
    return result.type.case === 'success' ? result.type.value.success : false;
  } catch (error) {
    logger.get('API').error('messaging.subscribeOnMessages', error);
    throw buildModelError(error);
  }
}

export async function getLinkPreview(link: string): Promise<ILinkPreviewModel | null> {
  try {
    const result = await getLinkPreviewRequest({ link });

    if (result.type.case === 'unableToCreateLinkPreview') {
      throw new WsRequestError(MessageModelErrorCode.UnableToCreateLinkPreview, result);
    }

    if (result.type.case !== 'success') {
      return null;
    }

    return mapILinkPreviewModel(result.type.value.linkPreview);
  } catch (error) {
    logger.get('API').error('messaging.getLinkPreview', error);
    throw buildModelError(error);
  }
}

export async function getUsersViewedMessage(
  data: IGetUsersViewedMessageRequest
): Promise<IGetUsersViewedMessageResponse> {
  try {
    const result = await getUsersViewedMessageRequest({
      ...data,
      chatId: BigInt(data.groupId),
      messageId: BigInt(data.messageId),
    });

    switch (result.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, result);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, result);
      case 'hasNoPermissionsToPerformAction':
        throw new WsRequestError(ServerErrorCode.HasNoPermissionsToPerformAction, result);
    }

    return {
      userIds: (result.type.value?.userIds || []).map((id) => String(id)),
      cursor: result.type.value?.cursor,
    };
  } catch (error) {
    logger.get('API').error('messaging.getUsersViewedMessage', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function translateMessage(data: ITranslateMessageRequest): Promise<IMessage> {
  try {
    return {} as IMessage;
  } catch (error) {
    logger.get('API').error('messaging.translateMessage', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation

export async function transcribeVoiceMessage(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  groupId: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  messageId: string
): Promise<IMessage> {
  try {
    return {} as IMessage;
  } catch (error) {
    logger.get('API').error('messaging.transcribeVoiceMessage', error);
    throw buildModelError(error);
  }
}

// TODO: не хватает метода, надо на беке реализовать этот метод
// Needs Bff implementation
export async function getDrafts(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  data: IGetMessageDraftsRequest
): Promise<IMessageDraftsAndTsResponse> {
  try {
    return {} as IMessageDraftsAndTsResponse;
  } catch (error) {
    logger.get('API').error('messaging.getDrafts', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function getDraft(data: IGetMessageDraftRequest): Promise<IMessageDraftsResponse> {
  try {
    return {} as IMessageDraftsResponse;
  } catch (error) {
    logger.get('API').error('messaging.getDraft', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function saveDraft(data: ISaveMessageDraftRequest): Promise<IMessage> {
  try {
    return {} as IMessage;
  } catch (error) {
    logger.get('API').error('messaging.saveDraft', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function updateDraft(data: IUpdateMessageDraftRequest): Promise<IMessage> {
  try {
    return {} as IMessage;
  } catch (error) {
    logger.get('API').error('messaging.updateDraft', error);
    throw buildModelError(error);
  }
}

// TODO: пока не инетерсно
// Needs Bff implementation
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function deleteDrafts(data: IDeleteMessageDraftsRequest): Promise<boolean> {
  try {
    return false;
  } catch (error) {
    logger.get('API').error('messaging.deleteDrafts', error);
    throw buildModelError(error);
  }
}

export async function getMessagesByMediaTypes(
  data: IGetMessagesByMediaTypesRequest
): Promise<IGetMessagesResponse> {
  try {
    const response = await getMessagesByMediaTypesRequest({
      mediaTypes: data.mediaTypes.map((type) => mapMessageMediaType(type)),
      direction: mapMessageDirection(data.direction),
      cursorTs: data.cursorTs ? BigInt(data.cursorTs) : undefined,
      chatId: BigInt(data.groupId),
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
    }

    return {
      messages: response.type.value?.items.map(mapIMessage) || [],
      cursorTs: response.type.value?.cursorTs?.toString(),
      maxTs: response.type.value?.maxTs?.toString(),
      minTs: response.type.value?.minTs?.toString(),
    };
  } catch (error) {
    logger.get('API').error('messaging.getMessagesByMediaTypes', error);
    throw buildModelError(error);
  }
}

export async function getMessagesByMediaTypesCount(
  data: IGetMessagesByMediaTypesCountRequest
): Promise<IGetMessagesByMediaTypesCountResponse> {
  try {
    const response = await getMessagesCountsRequest({ chatId: BigInt(data.groupId) });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
    }

    return mapMessagesCountsResponse(response.type.value?.counts || []);
  } catch (error) {
    logger.get('API').error('messaging.getMessagesByMediaTypes', error);
    throw buildModelError(error);
  }
}

export async function getMessagesByIds(
  groupId: string,
  messagesIds: string[]
): Promise<IMessage[]> {
  try {
    const response = await getMessagesByIdsRequest({
      chatId: BigInt(groupId),
      messageIds: messagesIds.map((id) => BigInt(id)),
    });

    switch (response.type.case) {
      case 'chatIsUnavailable':
        throw new WsRequestError(GroupServerErrorCode.GroupIsUnavailable, response);
      case 'userChatRelationshipDoesNotExist':
        throw new WsRequestError(GroupServerErrorCode.UserGroupRelationshipDoesNotExist, response);
    }

    return response.type.value?.items.map(mapIMessage) || [];
  } catch (error) {
    logger.get('API').error('messaging.getMessages', error);
    throw buildModelError(error);
  }
}

export async function deleteScheduledMessages(
  data: IDeleteScheduledMessagesRequest
): Promise<boolean> {
  try {
    const result = await deleteScheduledMessagesRequest({
      chatId: BigInt(data.groupId),
      messageIds: data.messageIds.map((id) => BigInt(id)),
    });

    return result.type.case === 'success';
  } catch (error) {
    logger.get('API').error('messaging.deleteScheduledMessages', error);
    throw buildModelError(error);
  }
}

export async function getScheduledMessage(
  groupId: string,
  messageId: string
): Promise<IScheduledMessage> {
  try {
    const result = await getScheduledMessagesByIdsRequest({
      chatId: BigInt(groupId),
      messageIds: [BigInt(messageId)],
    });

    if (result.type.case === 'success') {
      return result.type.value.scheduledMessages.map(mapScheduledMessageToIScheduledMessage)[0];
    }
    throw new Error();
  } catch (error) {
    logger.get('API').error('messaging.getScheduledMessage', error);
    throw buildModelError(error);
  }
}

export async function getGroupScheduledMessages(
  groupId: string,
  limit: number,
  cursor?: string
): Promise<IGetScheduledMessagesResponse> {
  try {
    const result = await getScheduledMessagesRequest({
      chatId: BigInt(groupId),
      limit: limit,
      cursor,
    });
    if (result.type.case === 'success') {
      return mapScheduledMessagesResponse(result.type.value);
    }
    throw new Error();
  } catch (error) {
    logger.get('API').error('messaging.getGroupScheduledMessages', error);
    throw buildModelError(error);
  }
}

export async function updateScheduledMessage(data: IUpdateScheduledMessageRequest): Promise<void> {
  try {
    await updateScheduledMessageRequest(mapUpdateScheduledMessageRequest(data));
  } catch (error) {
    logger.get('API').error('messaging.updateScheduledMessage', error);
    throw buildModelError(error);
  }
}

export async function saveScheduledMessage(data: ISendScheduledMessageRequest): Promise<void> {
  try {
    await scheduleMessageRequest(mapSaveScheduledMessageRequest(data));
  } catch (error) {
    logger.get('API').error('messaging.saveScheduledMessage', error);
    throw buildModelError(error);
  }
}
